import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Chip, 
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert
} from '@mui/material';
import { 
  Business, 
  TrendingUp, 
  LocationOn,
  Schedule,
  Assessment,
  Security
} from '@mui/icons-material';

const CommercialProperties = () => {
  const [filters, setFilters] = useState({
    propertyType: '',
    location: '',
    priceRange: '',
    size: ''
  });

  const propertyTypes = [
    {
      type: "Office Buildings",
      description: "Multi-tenant office spaces and professional buildings",
      averageROI: "12-18%",
      minInvestment: "$250K",
      tenantType: "Professional Services",
      riskLevel: "Medium",
      count: 34
    },
    {
      type: "Retail Spaces", 
      description: "Strip malls, standalone retail, and shopping centers",
      averageROI: "10-15%",
      minInvestment: "$150K",
      tenantType: "Retail Businesses", 
      riskLevel: "Medium-High",
      count: 28
    },
    {
      type: "Industrial/Warehouse",
      description: "Manufacturing facilities, warehouses, and distribution centers",
      averageROI: "15-22%",
      minInvestment: "$400K",
      tenantType: "Manufacturing/Logistics",
      riskLevel: "Low-Medium",
      count: 19
    },
    {
      type: "Mixed-Use",
      description: "Properties combining residential, retail, and office space",
      averageROI: "14-20%",
      minInvestment: "$300K",
      tenantType: "Multiple",
      riskLevel: "Medium",
      count: 15
    }
  ];

  const sampleProperties = [
    {
      id: 1,
      name: "Downtown Office Complex",
      type: "Office Building",
      location: "Newark, NJ",
      size: "25,000 sq ft",
      upsetValue: "$875,000",
      estimatedValue: "$1,200,000",
      currentNOI: "$145,000",
      capRate: "12.1%",
      occupancy: "85%",
      yearBuilt: 1998,
      auctionDate: "Jan 15, 2025"
    },
    {
      id: 2,
      name: "Strip Mall Center",
      type: "Retail",
      location: "Camden, NJ",
      size: "12,500 sq ft",
      upsetValue: "$425,000",
      estimatedValue: "$580,000",
      currentNOI: "$78,000",
      capRate: "13.4%",
      occupancy: "70%",
      yearBuilt: 2005,
      auctionDate: "Jan 18, 2025"
    },
    {
      id: 3,
      name: "Industrial Warehouse",
      type: "Industrial",
      location: "Elizabeth, NJ",
      size: "45,000 sq ft",
      upsetValue: "$1,250,000",
      estimatedValue: "$1,650,000",
      currentNOI: "$235,000",
      capRate: "14.2%",
      occupancy: "100%",
      yearBuilt: 2012,
      auctionDate: "Jan 22, 2025"
    }
  ];

  const investmentConsiderations = [
    {
      title: "Due Diligence Requirements",
      items: [
        "Environmental assessments (Phase I/II)",
        "Structural and engineering reports",
        "Tenant lease reviews and credit analysis",
        "Financial statements and operating history",
        "Zoning and permit compliance",
        "Market analysis and competitive landscape"
      ]
    },
    {
      title: "Financing Options",
      items: [
        "Commercial mortgages (70-80% LTV typical)",
        "SBA loans for owner-occupied properties",
        "Bridge financing for value-add opportunities",
        "CMBS (Commercial Mortgage-Backed Securities)",
        "Private lending and hard money",
        "Partnership and syndication structures"
      ]
    },
    {
      title: "Risk Factors",
      items: [
        "Tenant concentration and credit quality",
        "Market vacancy rates and absorption",
        "Economic sensitivity of tenant industries",
        "Property condition and capital requirements",
        "Environmental liabilities",
        "Interest rate fluctuations"
      ]
    }
  ];

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'Low': return 'success';
      case 'Low-Medium': return 'info';
      case 'Medium': return 'warning';
      case 'Medium-High': return 'error';
      case 'High': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h1" sx={{ 
            fontWeight: 700, 
            color: '#1B5E20',
            mb: 2 
          }}>
            Commercial Properties
          </Typography>
          <Typography variant="h6" sx={{ 
            color: '#666',
            maxWidth: '800px',
            mx: 'auto',
            lineHeight: 1.6
          }}>
            Discover commercial real estate opportunities with strong cash flow potential. 
            From office buildings to industrial properties, find your next investment.
          </Typography>
        </Box>

        {/* Key Benefits */}
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body1">
            <strong>Commercial Advantage:</strong> Commercial properties typically offer higher returns, 
            longer lease terms, and professional tenant relationships compared to residential investments.
          </Typography>
        </Alert>

        {/* Property Types */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 4, color: '#1B5E20' }}>
            Commercial Property Types
          </Typography>
          <Grid container spacing={3}>
            {propertyTypes.map((property, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ 
                  height: '100%',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-2px)' }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1B5E20' }}>
                        {property.type}
                      </Typography>
                      <Chip 
                        label={`${property.count} Available`} 
                        size="small" 
                        color="primary"
                      />
                    </Box>
                    
                    <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
                      {property.description}
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#666' }}>Avg ROI</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                          {property.averageROI}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#666' }}>Min Investment</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {property.minInvestment}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#666' }}>Tenant Type</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {property.tenantType}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#666' }}>Risk Level</Typography>
                        <Chip 
                          label={property.riskLevel} 
                          size="small" 
                          color={getRiskColor(property.riskLevel)}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Search Filters */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1B5E20' }}>
            Find Commercial Properties
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Property Type</InputLabel>
                <Select
                  value={filters.propertyType}
                  label="Property Type"
                  onChange={(e) => setFilters({...filters, propertyType: e.target.value})}
                >
                  <MenuItem value="office">Office Buildings</MenuItem>
                  <MenuItem value="retail">Retail Spaces</MenuItem>
                  <MenuItem value="industrial">Industrial/Warehouse</MenuItem>
                  <MenuItem value="mixed">Mixed-Use</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Location"
                placeholder="City, County"
                value={filters.location}
                onChange={(e) => setFilters({...filters, location: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Price Range</InputLabel>
                <Select
                  value={filters.priceRange}
                  label="Price Range"
                  onChange={(e) => setFilters({...filters, priceRange: e.target.value})}
                >
                  <MenuItem value="0-500k">$0 - $500K</MenuItem>
                  <MenuItem value="500k-1m">$500K - $1M</MenuItem>
                  <MenuItem value="1m-2m">$1M - $2M</MenuItem>
                  <MenuItem value="2m+">$2M+</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button 
                variant="contained" 
                fullWidth 
                sx={{ bgcolor: '#1B5E20', height: '56px' }}
              >
                Search Properties
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Sample Properties */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 4, color: '#1B5E20' }}>
            Featured Commercial Listings
          </Typography>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Property</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Upset Value</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>NOI</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Cap Rate</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Occupancy</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Auction Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sampleProperties.map((property) => (
                  <TableRow key={property.id} sx={{ '&:hover': { bgcolor: '#f8f9fa' } }}>
                    <TableCell>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {property.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          <LocationOn sx={{ fontSize: 14, mr: 0.5 }} />
                          {property.location}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{property.type}</TableCell>
                    <TableCell>{property.size}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1B5E20' }}>
                      {property.upsetValue}
                    </TableCell>
                    <TableCell>{property.currentNOI}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2e7d32' }}>
                      {property.capRate}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={property.occupancy}
                        size="small"
                        color={parseFloat(property.occupancy) >= 80 ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Schedule sx={{ fontSize: 14, mr: 0.5, color: '#666' }} />
                        {property.auctionDate}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="outlined" 
                        size="small"
                        sx={{ color: '#1B5E20', borderColor: '#1B5E20' }}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Investment Considerations */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 4, color: '#1B5E20' }}>
            Commercial Investment Guide
          </Typography>
          <Grid container spacing={4}>
            {investmentConsiderations.map((section, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1B5E20' }}>
                      {section.title}
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, m: 0 }}>
                      {section.items.map((item, itemIndex) => (
                        <Typography 
                          component="li" 
                          variant="body2" 
                          key={itemIndex}
                          sx={{ mb: 1, color: '#666' }}
                        >
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Commercial Metrics Explanation */}
        <Paper sx={{ p: 4, mb: 6, bgcolor: '#e8f5e8' }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#1B5E20' }}>
            Understanding Commercial Metrics
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Assessment sx={{ color: '#1B5E20', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Net Operating Income (NOI)</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Annual rental income minus operating expenses (excluding debt service). 
                Key metric for property valuation and cash flow analysis.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp sx={{ color: '#1B5E20', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Capitalization Rate</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#666' }}>
                NOI divided by property value. Helps compare properties and assess market values. 
                Higher cap rates generally indicate higher returns but potentially higher risk.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Security sx={{ color: '#1B5E20', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Occupancy Rate</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Percentage of occupied space. Higher occupancy typically means stable income, 
                but consider lease terms and tenant quality in your analysis.
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Call to Action */}
        <Box sx={{ 
          textAlign: 'center', 
          bgcolor: '#1B5E20', 
          color: 'white',
          borderRadius: 3,
          p: 4
        }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            Ready for Commercial Real Estate?
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
            Access our complete database of commercial foreclosure properties
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            sx={{ 
              bgcolor: 'white',
              color: '#1B5E20',
              fontWeight: 600,
              px: 4,
              mr: 2
            }}
          >
            Browse All Commercial
          </Button>
          <Button 
            variant="outlined" 
            size="large"
            sx={{ 
              color: 'white',
              borderColor: 'white',
              fontWeight: 600,
              px: 4
            }}
          >
            Download Commercial Guide
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default CommercialProperties;














